import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Chip,
  LinearProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Slide,
  List,
  ListItem,
  Button,
  CircularProgress,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { forwardRef } from 'react';
import {
  Close as CloseIcon,
  ViewModule as ViewModuleIcon,
  Check as CheckIcon,
  Circle as DotIcon,
  MonitorHeart as DashboardIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  Terminal as TerminalIcon,
  TouchApp as ActivityIconMat,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import {
  useSharedDeviceStream,
  useDeviceLiveLogging,
  useDeviceMemoryMonitor,
} from '@/contexts/device-stream-context';
import { deviceService } from '@/lib/device.service';
import { PanelFrame, usePanelVisibility } from './panel-frame';
import { DashboardGrid, useDashboardLayout, type GridPanel } from './dashboard-grid';
import { LogViewer } from './log-viewer';
import { MemoryMonitor } from './memory-monitor';
import { FormattedTime } from './ui/formatted-time';
import { InternalDeviceIcon } from './ui/internal-device-icon';
import type { Device } from '@/types';
import { colors } from '@/theme/tokens';

// Panel registry. `gridSize` uses MUI Grid 2 size shape; panels that
// carry large content (log stream, memory chart) span wider; gauges are
// narrow so four fit on one row.
type PanelId =
  | 'status'
  | 'cpu'
  | 'memory'
  | 'storage'
  | 'logs'
  | 'memchart'
  | 'activity';

interface PanelDef {
  id: PanelId;
  title: string;
  icon: React.ReactNode;
  gridSize: { xs: number; sm: number; md: number; lg?: number };
}

const PANELS: PanelDef[] = [
  { id: 'status', title: 'Status', icon: <DotIcon sx={{ fontSize: 16 }} />, gridSize: { xs: 12, sm: 6, md: 3 } },
  { id: 'cpu', title: 'CPU', icon: <SpeedIcon sx={{ fontSize: 16 }} />, gridSize: { xs: 6, sm: 3, md: 3 } },
  { id: 'memory', title: 'Memory', icon: <MemoryIcon sx={{ fontSize: 16 }} />, gridSize: { xs: 6, sm: 3, md: 3 } },
  { id: 'storage', title: 'Storage', icon: <StorageIcon sx={{ fontSize: 16 }} />, gridSize: { xs: 12, sm: 6, md: 3 } },
  { id: 'logs', title: 'Live Log Stream', icon: <TerminalIcon sx={{ fontSize: 16 }} />, gridSize: { xs: 12, sm: 12, md: 8 } },
  { id: 'memchart', title: 'Memory over time', icon: <MemoryIcon sx={{ fontSize: 16 }} />, gridSize: { xs: 12, sm: 12, md: 4 } },
  { id: 'activity', title: 'Activity', icon: <ActivityIconMat sx={{ fontSize: 16 }} />, gridSize: { xs: 12, sm: 12, md: 12 } },
];

const DEFAULT_VISIBILITY: Record<PanelId, boolean> = {
  status: true,
  cpu: true,
  memory: true,
  storage: true,
  logs: true,
  memchart: true,
  // Hidden by default: the Activity Monitor is a separate device-side feature
  // (enabled from the Diagnostics tab) that Start does not turn on, so a
  // default-on panel would sit empty in most sessions. Operators can re-enable
  // it from the panel menu once activity monitoring is running.
  activity: false,
};

// PanelFrame + usePanelVisibility moved to '@/components/panel-frame' so the
// Fleet Analytics dashboard shares the same chrome/persistence primitives.

// Default draggable-grid layout (12 cols, ~48px rows): four gauges across the
// top, log stream + memory chart below, activity full-width. Users drag/resize
// freely; layout persists per-browser with a Reset.
const DASH_LAYOUT: Record<PanelId, { x: number; y: number; w: number; h: number; minW?: number; minH?: number }> = {
  status: { x: 0, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
  cpu: { x: 3, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
  memory: { x: 6, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
  storage: { x: 9, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
  logs: { x: 0, y: 3, w: 8, h: 10, minW: 4, minH: 5 },
  memchart: { x: 8, y: 3, w: 4, h: 10, minW: 3, minH: 5 },
  activity: { x: 0, y: 13, w: 12, h: 6, minW: 3, minH: 3 },
};
// Stable list for the layout hook (defaults/reset only need id + position).
const LAYOUT_PANELS: GridPanel[] = PANELS.map((p) => ({ id: p.id, ...DASH_LAYOUT[p.id], render: () => null }));

// Compact "time ago" for heartbeat freshness — seconds under a minute, then m/h.
function fmtAge(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
}

function Gauge({ value, max, unit, thresholds = [70, 90], percentOnly = false, stale = false }: { value: number | null; max?: number | null; unit?: string; thresholds?: [number, number]; percentOnly?: boolean; stale?: boolean }) {
  const pct = value != null && max && max > 0 ? Math.min(100, (value / max) * 100) : value != null ? Math.min(100, value) : null;
  const color = pct == null
    ? '#BDBDBD'
    : pct >= thresholds[1]
    ? colors.error.main
    : pct >= thresholds[0]
    ? colors.warning.main
    : colors.success.main;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 0.5, opacity: stale ? 0.5 : 1 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color, fontFamily: 'monospace', lineHeight: 1 }}>
        {pct == null ? '—' : `${pct.toFixed(0)}%`}
      </Typography>
      {!percentOnly && value != null && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {max && max > 0 ? `${value.toLocaleString()} / ${max.toLocaleString()} ${unit || ''}` : `${value.toLocaleString()} ${unit || ''}`}
        </Typography>
      )}
      <LinearProgress
        variant="determinate"
        value={pct ?? 0}
        sx={{
          width: '80%',
          height: 8,
          borderRadius: 1,
          bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 1 },
        }}
      />
      {stale && pct != null && (
        <Typography variant="caption" sx={{ color: 'warning.main', fontFamily: 'monospace', fontWeight: 700, fontSize: 10, letterSpacing: 0.4 }}>
          ⚠ stale
        </Typography>
      )}
    </Box>
  );
}

// ─── df -h parsing (mirrors storage-explorer.tsx) ────────────────────

interface DfRow {
  totalBytes: number;
  usedBytes: number;
  availableBytes: number;
  usePercent: number;
  mountedOn: string;
}

function parseHumanBytes(sizeStr: string): number {
  const match = sizeStr.match(/^([\d.]+)([BKMGTP]?)$/i);
  if (!match) return 0;
  const val = parseFloat(match[1]);
  const unit = (match[2] || 'B').toUpperCase();
  const mult: Record<string, number> = { B: 1, K: 1024, M: 1024 ** 2, G: 1024 ** 3, T: 1024 ** 4, P: 1024 ** 5 };
  return val * (mult[unit] || 1);
}

// Pick the most relevant row from `df -h`. /data is where the MDM agent
// writes, so it's the row a viewer cares about; rootfs on Android is a
// tiny system partition that's almost full and misleading. Falls back
// to '/' then to the first parseable row if /data isn't mounted.
function parseDf(stdout: string): DfRow | null {
  const lines = stdout.trim().split('\n');
  if (lines.length < 2) return null;
  let dataRow: DfRow | null = null;
  let rootRow: DfRow | null = null;
  let firstRow: DfRow | null = null;
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].trim().split(/\s+/);
    if (parts.length < 6) continue;
    const usePercent = parseInt(parts[4].replace('%', ''), 10);
    if (isNaN(usePercent)) continue;
    const row: DfRow = {
      totalBytes: parseHumanBytes(parts[1]),
      usedBytes: parseHumanBytes(parts[2]),
      availableBytes: parseHumanBytes(parts[3]),
      usePercent,
      mountedOn: parts.slice(5).join(' '),
    };
    if (row.mountedOn === '/data') dataRow = row;
    else if (row.mountedOn === '/' && !rootRow) rootRow = row;
    if (!firstRow) firstRow = row;
  }
  return dataRow || rootRow || firstRow;
}

// ─── Individual panels ───────────────────────────────────────────────

function StoragePanel({ deviceId, active, fallbackTotal, fallbackUsed }: {
  deviceId: string;
  // Streaming flag from the dashboard. When it flips to true (Start),
  // we kick off a one-shot df. We don't poll because filesystem totals
  // change slowly and `df -h` over MQTT isn't free.
  active: boolean;
  // Cached numbers from device.info.storage (last status frame). Used
  // only as a placeholder while df hasn't returned yet — once df runs
  // its numbers replace these.
  fallbackTotal?: number;
  fallbackUsed?: number;
}) {
  const [df, setDf] = useState<DfRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await deviceService.executeShellCommand(deviceId, { command: 'df -h', timeoutMs: 15000 });
      if (!result.success || !result.response) {
        throw new Error(result.response?.stderr || 'Command failed');
      }
      const resp = result.response;
      if (resp.exitCode !== 0) {
        throw new Error(resp.stderr || `Exit code: ${resp.exitCode}`);
      }
      const parsed = parseDf(resp.stdout || '');
      if (!parsed) throw new Error('No filesystem entries found in df output');
      setDf(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to query storage');
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  // Auto-refresh on Start (active false→true). Re-runs on each Start
  // cycle so a long-open dialog doesn't show stale numbers, but doesn't
  // poll while active. Reset flag on Stop so the next Start refetches.
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (active && !fetchedRef.current) {
      fetchedRef.current = true;
      refresh();
    } else if (!active) {
      fetchedRef.current = false;
    }
  }, [active, refresh]);

  const total = df?.totalBytes ?? fallbackTotal ?? null;
  const used = df?.usedBytes ?? fallbackUsed ?? null;
  // Convert bytes → GB for the gauge readout. Raw byte counts in the
  // 10-12 digit range render unreadably in the small "value / max"
  // caption; GB with one decimal stays compact ("12.3 / 64 GB").
  const totalGb = total != null ? Number((total / 1024 ** 3).toFixed(1)) : null;
  const usedGb = used != null ? Number((used / 1024 ** 3).toFixed(1)) : null;
  const hasData = totalGb != null && usedGb != null && totalGb > 0;

  if (!hasData) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'center', m: 'auto', px: 1 }}>
        <Typography variant="caption" color={error ? 'error.main' : 'text.secondary'}>
          {error || 'No storage snapshot yet. Run df -h on the device to populate.'}
        </Typography>
        <Button
          size="small"
          startIcon={loading ? <CircularProgress size={12} color="inherit" /> : <RefreshIcon sx={{ fontSize: 14 }} />}
          onClick={refresh}
          disabled={!deviceId || loading}
          sx={{ alignSelf: 'center', textTransform: 'none' }}
        >
          {loading ? 'Running…' : 'Run df -h'}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 0.25 }}>
      <Gauge value={usedGb!} max={totalGb!} unit="GB" />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        {df?.mountedOn && (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', fontSize: 10 }}>
            {df.mountedOn}
          </Typography>
        )}
        <Button
          size="small"
          onClick={refresh}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={10} color="inherit" /> : <RefreshIcon sx={{ fontSize: 12 }} />}
          sx={{ textTransform: 'none', fontSize: 10, py: 0, minHeight: 20 }}
        >
          Refresh
        </Button>
      </Box>
    </Box>
  );
}

function StatusPanel({ device, wsConnected, lastBeatLabel, stale }: {
  device: Device;
  wsConnected: boolean;
  // Heartbeat freshness ("beat 12s ago" / "no beat 3m") — device liveness,
  // distinct from the browser↔server socket state (wsConnected).
  lastBeatLabel?: string;
  stale?: boolean;
}) {
  const statusColor = device.status === 'online' ? colors.status.online.color
    : device.status === 'away' ? colors.status.pending.color
    : device.status === 'offline' ? '#999'
    : colors.status.blocked.color;
  const beatColor = stale ? colors.warning.main : colors.status.online.color;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={device.status}
          size="small"
          sx={{ bgcolor: statusColor, color: '#fff', fontWeight: 700, textTransform: 'uppercase', fontSize: 10 }}
        />
        {lastBeatLabel ? (
          <Chip
            label={lastBeatLabel}
            size="small"
            variant="outlined"
            sx={{ fontSize: 10, fontWeight: stale ? 700 : 400, borderColor: beatColor, color: beatColor }}
          />
        ) : (
          <Chip
            label={wsConnected ? 'WS live' : 'WS offline'}
            size="small"
            variant="outlined"
            sx={{ fontSize: 10, borderColor: wsConnected ? colors.status.online.color : colors.status.blocked.color, color: wsConnected ? colors.status.online.color : colors.status.blocked.color }}
          />
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.5, fontSize: 12, fontFamily: 'monospace' }}>
        <Typography variant="caption" color="text.secondary">Device</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" fontWeight={600}>{(device.number || device.serial_number || device.id).toUpperCase()}</Typography>
          <InternalDeviceIcon internal={device.is_internal} size={13} />
        </Box>
        <Typography variant="caption" color="text.secondary">Type</Typography>
        <Typography variant="caption">{device.type || '—'}</Typography>
        <Typography variant="caption" color="text.secondary">OS</Typography>
        <Typography variant="caption">{device.os_version || '—'}</Typography>
        {/* Firmware identity — the recurring "which build is misbehaving?"
            fields. Already on the device object, no extra fetch. */}
        <Typography variant="caption" color="text.secondary">Agent</Typography>
        <Typography variant="caption">{device.agent_version || '—'}</Typography>
        <Typography variant="caption" color="text.secondary">MDM</Typography>
        <Typography variant="caption">{device.mdm_version_info?.version || '—'}</Typography>
        <Typography variant="caption" color="text.secondary">ROM</Typography>
        <Typography variant="caption">{device.rom_version || '—'}</Typography>
      </Box>
    </Box>
  );
}

function LogsPanel({
  getLogs,
  logsVersion,
}: {
  getLogs: ReturnType<typeof useSharedDeviceStream>['getLogs'];
  logsVersion: ReturnType<typeof useSharedDeviceStream>['logsVersion'];
}) {
  return (
    <Box sx={{ flex: 1, minHeight: 0 }}>
      {/* Keep the level + type filters so an operator can cut a chatty
          info-level stream down to errors/crashes without leaving the overlay. */}
      <LogViewer
        getLogs={getLogs}
        logsVersion={logsVersion}
        height={420}
        isLive
      />
    </Box>
  );
}

function MemoryChartPanel({ reports }: { reports: ReturnType<typeof useSharedDeviceStream>['memoryReports'] }) {
  if (!reports || reports.length === 0) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" color="text.secondary">Waiting for memory samples…</Typography>
      </Box>
    );
  }
  return <MemoryMonitor memoryReports={reports} isLive height={320} />;
}

function ActivityPanel({
  getLogs,
  logsVersion,
}: {
  getLogs: ReturnType<typeof useSharedDeviceStream>['getLogs'];
  logsVersion: ReturnType<typeof useSharedDeviceStream>['logsVersion'];
}) {
  const activity = useMemo(
    () => getLogs().filter((l) => l.log_type === 'activity').slice(0, 12),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logsVersion, getLogs],
  );
  if (activity.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
        No recent activity events. Start Activity Monitor from the Diagnostics tab to populate.
      </Typography>
    );
  }
  return (
    <List disablePadding sx={{ overflow: 'auto', maxHeight: 320 }}>
      {activity.map((a) => (
        <ListItem key={a.id} divider dense sx={{ py: 0.5, px: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
              {(a.metadata?.packageName as string) || '—'}
              {a.metadata?.activityName ? ` • ${a.metadata.activityName as string}` : ''}
            </Typography>
            <FormattedTime value={a.created_at} variant="relative" sx={{ fontSize: 10, color: 'text.secondary' }} />
          </Box>
        </ListItem>
      ))}
    </List>
  );
}

// ─── Main Dialog ─────────────────────────────────────────────────────

const SlideUp = forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface DeviceLiveDashboardProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
}

export function DeviceLiveDashboard({ open, onClose, device }: DeviceLiveDashboardProps) {
  const [visibility, togglePanel] = usePanelVisibility<PanelId>('device-dashboard-panels', DEFAULT_VISIBILITY);
  const [panelMenuAnchor, setPanelMenuAnchor] = useState<HTMLElement | null>(null);
  // Streaming gate. Opening the dialog alone does NOT start the WS feed
  // — the user must click Start. This prevents the backend from being
  // hammered every time someone glances at the dashboard, and lets the
  // user inspect panel layout/configuration before committing to a
  // live session. Resets on close so the next open starts paused.
  const [started, setStarted] = useState(false);
  // Draggable/resizable layout (persisted per-browser). Uses the full panel
  // list so hidden panels keep their saved slot when re-shown.
  const { layouts, onLayoutChange, reset: resetLayout } = useDashboardLayout('device-dashboard-layout', LAYOUT_PANELS);

  // Device-side feature gates. WS-level set_logs/set_memory only tell
  // the hub to deliver frames to this client; the device itself doesn't
  // emit until live-logging / memory-monitor are turned on. We declare
  // those wants via the provider's refcounted helper hooks rather than
  // calling enable/disable directly so the page's own Live Logs / Memory
  // toggles can coexist — only the LAST consumer to release actually
  // tears the device-side stream down.
  // Stop is global: if either device-global session ends for a reason this
  // dialog didn't cause, come off `started` so the button stops claiming to be
  // streaming. `started` drives both wants, so either one ending stops both —
  // which matches the single Start/Stop control the dialog presents.
  const { pending: liveLoggingPending } = useDeviceLiveLogging(
    started,
    { level: 'info' },
    () => setStarted(false),
  );
  const { pending: memoryMonitorPending } = useDeviceMemoryMonitor(
    started,
    { threshold: 102400 },
    () => setStarted(false),
  );
  // Both toggle on the same `started` flag, so block the button while either
  // enable/disable is in flight — stops spam-clicking from storming the device.
  const togglePending = liveLoggingPending || memoryMonitorPending;

  const handleStart = useCallback(() => setStarted(true), []);
  const handleStop = useCallback(() => setStarted(false), []);
  const handleClose = useCallback(() => {
    setStarted(false);
    onClose();
  }, [onClose]);

  // Live data. Enabled only while the Dialog is open AND the user has
  // pressed Start. Wants are forwarded to the shared provider so logs
  // and memory frames piggyback the page's existing WebSocket instead
  // of opening a second one (see DeviceStreamProvider in
  // crm-ui/src/contexts/device-stream-context.tsx). Status is always
  // available because the provider keeps the WS connected for the
  // header chip.
  const streaming = open && started;
  const {
    getLogs,
    logsVersion,
    memoryReports,
    status: rtStatus,
    isConnected,
  } = useSharedDeviceStream({
    logs: streaming,
    memory: streaming,
  });

  // Heartbeat freshness clock. The gauges/subtitle report device liveness from
  // the last heartbeat's timestamp, but no re-render happens while a device is
  // silent — so a dead device would freeze at "last beat 5s ago". Tick every 5s
  // while streaming so the age recomputes and staleness can flip on its own.
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!streaming) return;
    setNowTick(Date.now());
    const id = setInterval(() => setNowTick(Date.now()), 5000);
    return () => clearInterval(id);
  }, [streaming]);

  if (!device) return null;

  // Memory total comes from the memory monitor stream (its samples
  // carry totalKb). The heartbeat (rtStatus.memory_kb) only carries
  // used. `device.info.memory_total` exists in the type but the backend
  // never populates it, so without the latest memory report we have
  // no denominator → a percent gauge would mis-clamp to 100%. Pick the
  // freshest report and use its totalKb; show a percent-less readout
  // until one arrives.
  const latestMemReport = memoryReports.length > 0 ? memoryReports[memoryReports.length - 1] : null;
  const memTotalKb = latestMemReport?.totalKb;
  const memUsedKb = rtStatus?.memory_kb ?? latestMemReport?.usedKb ?? null;
  const storageTotal = device.info?.storage?.total_bytes;
  const storageUsed = device.info?.storage?.used_bytes;

  // Device liveness from the last heartbeat, NOT the browser socket. Past ~2×
  // the nominal 60s interval with no beat we treat the readings as stale and
  // dim the gauges, so a live socket over a dead device can't read as healthy.
  const HEARTBEAT_STALE_SEC = 120;
  const lastBeatMs = rtStatus?.timestamp ? new Date(rtStatus.timestamp).getTime() : null;
  const lastBeatAgeSec = lastBeatMs != null ? Math.max(0, Math.round((nowTick - lastBeatMs) / 1000)) : null;
  const awaitingFirstBeat = streaming && isConnected && lastBeatAgeSec == null;
  const heartbeatStale = streaming && isConnected && lastBeatAgeSec != null && lastBeatAgeSec > HEARTBEAT_STALE_SEC;
  const lastBeatLabel = !streaming
    ? undefined
    : awaitingFirstBeat
    ? 'waiting…'
    : lastBeatAgeSec != null
    ? heartbeatStale
      ? `no beat ${fmtAge(lastBeatAgeSec)}`
      : `beat ${fmtAge(lastBeatAgeSec)} ago`
    : undefined;

  const DENSE_PANELS = new Set<PanelId>(['logs', 'memchart']);
  const panelContent = (id: PanelId): React.ReactNode => {
    switch (id) {
      case 'status':
        return <StatusPanel device={device} wsConnected={isConnected} lastBeatLabel={lastBeatLabel} stale={heartbeatStale} />;
      case 'cpu':
        return <Gauge value={rtStatus?.cpu_percent ?? null} percentOnly stale={heartbeatStale} />;
      case 'memory':
        return memTotalKb && memTotalKb > 0 ? (
          <Gauge value={memUsedKb != null ? Math.round(memUsedKb / 1024) : null} max={Math.round(memTotalKb / 1024)} unit="MB" percentOnly stale={heartbeatStale} />
        ) : (
          <Gauge value={null} percentOnly />
        );
      case 'storage':
        return <StoragePanel deviceId={device.id} active={streaming} fallbackTotal={storageTotal} fallbackUsed={storageUsed} />;
      case 'logs':
        return isConnected ? (
          <LogsPanel getLogs={getLogs} logsVersion={logsVersion} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1 }}>
            <CircularProgress size={16} sx={{ color: colors.primary.main }} />
            <Typography variant="caption" color="text.secondary">Connecting WebSocket…</Typography>
          </Box>
        );
      case 'memchart':
        return <MemoryChartPanel reports={memoryReports} />;
      case 'activity':
        return <ActivityPanel getLogs={getLogs} logsVersion={logsVersion} />;
    }
  };
  const gridPanels: GridPanel[] = PANELS.filter((p) => visibility[p.id]).map((p) => ({
    id: p.id,
    ...DASH_LAYOUT[p.id],
    render: () => (
      <PanelFrame title={p.title} icon={p.icon} draggable fill dense={DENSE_PANELS.has(p.id)}>
        {panelContent(p.id)}
      </PanelFrame>
    ),
  }));

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={SlideUp}
      PaperProps={{ sx: { bgcolor: 'background.default' } }}
    >
      <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Toolbar sx={{ gap: 2 }}>
          <DashboardIcon sx={{ color: colors.primary.main }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                Live Dashboard · {device.number || device.serial_number || device.id}
              </Typography>
              <InternalDeviceIcon internal={device.is_internal} size={16} />
            </Box>
            <Typography variant="caption" color={heartbeatStale ? 'warning.main' : 'text.secondary'} fontWeight={heartbeatStale ? 700 : 400} noWrap>
              {!started
                ? '○ Idle. Press Start to begin streaming'
                : !isConnected
                ? '○ Connecting…'
                : awaitingFirstBeat
                ? '● Connected · waiting for first heartbeat'
                : heartbeatStale
                ? `⚠ No heartbeat for ${fmtAge(lastBeatAgeSec!)} — device may be offline`
                : `● Live · last beat ${fmtAge(lastBeatAgeSec!)} ago`}
              {' • '}{device.type || 'device'}
              {device.os_version ? ` • ${device.os_version}` : ''}
            </Typography>
          </Box>
          {/* Start / Stop. Stop only pauses streaming and clears the
              live buffer for the next session — it doesn't close the
              dialog. Closing the dialog resets started=false so the
              next open is paused. */}
          <Button
            variant={started ? 'outlined' : 'contained'}
            size="small"
            disabled={togglePending}
            startIcon={started ? <StopIcon /> : <PlayIcon />}
            onClick={() => {
              if (togglePending) return;
              if (started) handleStop();
              else handleStart();
            }}
            sx={
              started
                ? { borderColor: colors.error.main, color: colors.error.main, '&:hover': { borderColor: '#C8102E', bgcolor: 'rgba(230,10,50,0.06)' } }
                : { bgcolor: colors.primary.main, '&:hover': { bgcolor: colors.primary.hover } }
            }
          >
            {started ? 'Stop' : 'Start'}
          </Button>
          <Tooltip title="Show/hide panels">
            <IconButton size="small" onClick={(e) => setPanelMenuAnchor(e.currentTarget)}>
              <ViewModuleIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={panelMenuAnchor}
            open={!!panelMenuAnchor}
            onClose={() => setPanelMenuAnchor(null)}
            slotProps={{ paper: { sx: { minWidth: 200 } } }}
          >
            <Typography variant="caption" sx={{ display: 'block', px: 2, pt: 0.5, pb: 0.25, color: 'text.disabled', fontWeight: 700, letterSpacing: 0.5 }}>
              PANELS
            </Typography>
            {PANELS.map((p) => (
              <MenuItem key={p.id} dense onClick={() => togglePanel(p.id)}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {visibility[p.id] ? <CheckIcon sx={{ fontSize: 16, color: colors.primary.main }} /> : null}
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{p.title}</ListItemText>
              </MenuItem>
            ))}
            <Divider sx={{ my: 0.5 }} />
            <MenuItem dense onClick={() => { resetLayout(); setPanelMenuAnchor(null); }}>
              <ListItemIcon sx={{ minWidth: 28 }}><RefreshIcon sx={{ fontSize: 16 }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Reset layout</ListItemText>
            </MenuItem>
            <Typography variant="caption" sx={{ display: 'block', px: 2, py: 0.5, color: 'text.disabled' }}>
              Drag panels by their title bar; resize from the corner. Layout persists in this browser.
            </Typography>
          </Menu>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, overflow: 'auto' }}>
        {/* Pre-start hint. The live-data panels still render below in
            their empty/idle states (gauges show "—", log stream shows
            "Waiting…"), but a prominent banner here makes the gate
            obvious so a non-tech viewer doesn't think the dashboard is
            broken. */}
        {!started && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 2,
              p: 1.5,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,103,32,0.12)' : '#FFF8F1',
              border: '1px solid',
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,103,32,0.35)' : '#FFD9BD',
              borderRadius: 1,
            }}
          >
            <PlayIcon sx={{ color: colors.primary.main }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>Live monitoring is paused</Typography>
              <Typography variant="caption" color="text.secondary">
                Press <strong>Start</strong> in the toolbar to open a WebSocket and begin streaming logs / memory / heartbeat from this device.
              </Typography>
            </Box>
          </Box>
        )}

        {gridPanels.length > 0 ? (
          <DashboardGrid panels={gridPanels} layouts={layouts} onLayoutChange={onLayoutChange} />
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="text.secondary">
              All panels are hidden. Open the panel menu (top-right) to enable them.
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
